import React, { useState, useEffect, useRef } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAward,
  faEnvelope,
  faPhone,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import placeholder from "../assets/images/house-placeholder.webp";
import { Link } from "react-router-dom";
import imgs from "./temp.json";
import ReactImageGallery from "react-image-gallery";
import calc from "../assets/images/shutterstock_2489188913-min.jpg";
import report from "../assets/images/shutterstock_699442570-min.jpg";
import house from "../assets/images/shutterstock_2493365641-min.jpg";
import constants from "../constants/constants";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import globalState from "../utils/globalState";
import { useUserParams } from "../hooks/useUserParams";
import DEMO_LISTINGS from "../constants/demoListings";

// Dynamically import all images from the images folder
const imageContext = require.context("../assets/images", false, /\.(jpg|jpeg|png|webp)$/);
const imageMap = {};
imageContext.keys().forEach((item) => {
  const imageName = item.replace("./", "");
  imageMap[imageName] = imageContext(item);
});

// Function to get the first image that contains the ListingKey in its filename
const getListingImage = (listingKey) => {
  if (!listingKey) return placeholder;
  
  // Find the first image file that contains the ListingKey in its name
  const matchingImage = Object.keys(imageMap).find((imageName) =>
    imageName.includes(listingKey)
  );
  
  return matchingImage ? imageMap[matchingImage] : placeholder;
};

export default function Homepage() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";

  const [listings, setListings] = useState([]);
  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const loaded = useRef(false);
  const loadedRef = useRef(false);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [formData, setFormData] = useState({
    Name: "",
    "Email Address": "",
    Message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentQuote,
    getCurrentHeadshot
  } = useUserParams();

  // Get userId from URL parameters
  const getURLParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      userId: urlParams.get('userId')
    };
  };

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation function
  const validateForm = () => {
    const errors = {};
    
    // Check if Name is empty
    if (!formData.Name.trim()) {
      errors.Name = "Name is required";
    }
    
    // Check if Email is empty and valid format
    if (!formData["Email Address"].trim()) {
      errors["Email Address"] = "Email is required";
    } else if (!validateEmail(formData["Email Address"])) {
      errors["Email Address"] = "Please enter a valid email address";
    }
    
    // Check if Message is empty
    if (!formData.Message.trim()) {
      errors.Message = "Message is required";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function submitForm(event) {
    event.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    const json = JSON.stringify({
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Contact Form Message",
    });

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: json,
      });

      const data = await res.json();

      if (data?.success) {
        console.log("Success", data);
        // Clear form after successful submission
        setFormData({
          Name: "",
          "Email Address": "",
          Message: "",
        });
        setFormErrors({});
        alert("Thanks for your message! We will get back to you as soon as possible.");
      } else {
        alert("We encountered an error in sending this message. Please try again later.");
      }
    } catch (err) {
      console.log(err);
      alert("We encountered an error in sending this message. Please try again later.");
    }
    
    setIsSubmitting(false);
  }

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);


  function getData() {
    // Skip API calls during static generation
    if (isReactSnap()) {
      return Promise.resolve({ value: [] });
    }

    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=(ListAgentKey eq '${constants.AGENT_ID}' and StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Property(Q)$filter=(ListAgentKey(S)eq(S)\'${constants.AGENT_ID}\'(S)and(S)StandardStatus(S)eq(S)\'Active\')(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;


        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${request}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          // Check if we have valid data
          if (res && res.value && res.value.length > 0) {
            resolve(res);
          } else {
            // Return demo data if API returns empty result
            console.warn("API returned empty data, using demo listings");
            resolve({ value: DEMO_LISTINGS });
          }
        } else {
          // Return demo data if API call fails
          console.warn("API call failed, using demo listings");
          resolve({ value: DEMO_LISTINGS });
        }
      } catch (err) {
        // Return demo data on error
        console.warn("Error fetching listings, using demo data:", err);
        resolve({ value: DEMO_LISTINGS });
      }
    });
  }

  function getMedia(listings) {
    // Skip API calls during static generation
    if (isReactSnap() || !listings || listings.length === 0) {
      return Promise.resolve({ value: [] });
    }

    // this is for development
    //var temp = `https://query.ampre.ca/odata/Media?$filter=(`

    // this is for production
    // var temp = `odata/Media?$filter=(`

    if (constants.ENVIRONMENT === "development") {
      var temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "', ";
      }
      temp = temp.slice(0, -2);
      temp =
        temp +
        ") and ImageSizeDescription eq 'Medium' and PreferredPhotoYN eq true";
    } else {
      var temp = `odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "',";
      }
      temp = temp.slice(0, -1);
      temp =
        temp +
        ")(S)and(S)ImageSizeDescription(S)eq(S)'Medium'(S)and(S)PreferredPhotoYN(S)eq(S)true";
    }
    var body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };

    return new Promise(async (resolve, reject) => {
      try {
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${temp}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    return temp;
  }

  // Function to fetch template settings from MongoDB
  const fetchTemplateSettings = async (userId) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(`https://agent.proagentsolutions.com/MongoDB/GetTemplateSettingsByUserId?userId=${userId}&apiKey=${apiKey}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const templateSettings = await response.json();
      console.log('Template settings fetched:', templateSettings);
      
      // Update global state with template settings
      if (templateSettings) {
        // Extract data from templateSettings.data if it exists
        const settingsData = templateSettings.data || templateSettings;
        
        // Map template settings to userData format
        const mappedUserData = {
          userId: userId,
          agentName: settingsData.agentName || settingsData.AGENT_NAME,
          agentTitle: settingsData.agentTitle || settingsData.AGENT_TITLE,
          agentNumber: settingsData.agentNumber || settingsData.AGENT_NUMBER,
          agentEmail: settingsData.agentEmail || settingsData.AGENT_EMAIL,
          brokerage: settingsData.brokerage || settingsData.BROKERAGE,
          // Include any additional fields from templateSettings.data
          ...settingsData
        };
        
        // Set the mapped user data
        globalState.setUserData(mappedUserData);
        
        console.log('Template settings data:', settingsData);
        console.log('Template settings mapped to user data:', mappedUserData);
      }
      
      return templateSettings;
    } catch (error) {
      console.error('Error fetching template settings:', error);
      return null;
    }
  };

  useEffect(() => {
    if (!loaded.current) {
      // Get and store URL parameters
      const urlParams = getURLParameters();
      if (urlParams.userId) {
        // Set global userId
        globalState.setUserId(urlParams.userId);
        
        // Fetch additional template settings from MongoDB
        // This will also update userData and userParams cache
        fetchTemplateSettings(urlParams.userId).then(() => {
          // Debug information after template settings are loaded
          console.log('Global State Updated with Template Settings:', globalState.getDebugInfo());
        });
        
        // Debug information
        console.log('Global State Updated:', globalState.getDebugInfo());
      }

      var temp1 = [];
      getData()
        .then((data) => {
          // data.value[0].ListingKey = 'W9297050';
          setListings(data.value);
          getMedia(data.value)
            .then((d) => {
              setMedia(d.value);
              temp1 = d.value;
              setFormatted(formatMedia(d.value));
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });

      // getMedia().then(data => {
      //     console.log(data)
      //     setMedia(data.value)

      // }).catch(err => {
      //     console.log(err);
      // })
      // var temp = {}

      // for (let img of temp1) {
      //     if (temp[img.ResourceRecordKey]) {
      //         temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img]
      //     } else {
      //         temp[img.ResourceRecordKey] = [img]

      //     }
      // }
      // console.log(temp)
      // setFormatted(temp);
      loaded.current = true;
    }
  }, []);

  const seoConfig = pageSEO.home();

  return (
    <>
      <SEO {...seoConfig} />
      <div
        className="w-full bg-no-repeat bg-cover min-h-[90vh] relative"
        style={{ backgroundImage: `url(${constants.HOME_IMAGE})` }}
      >
        <div className="p-12 bg-logo-main w-min absolute top-[30%] left-[10%]">
          <h1 className="text-white text-3xl sm:text-5xl font-mont">
            {getCurrentAgentName()}
          </h1>
          <h4 className="text-white text-xl font-mont mt-5 whitespace-normal">
            {getCurrentBrokerage()}
          </h4>
        </div>
      </div>
      <h1 className="text-4xl text-black py-8 font-mont text-center mt-6">
        AWARDS
      </h1>
      <div className="grid mx-auto sm:grid-cols-3 w-fit gap-x-12 gap-y-12 child:max-w-64 child:flex child:flex-col child:gap-y-5 child:text-center ">
        <div>
          <FontAwesomeIcon icon={faAward} className="fa-5x text-logo-second " />
          <span className="text-logo-second font-semibold text-2xl">2022</span>
          <span className="text-black font-semibold text-xl">
            Best Realtor Toronto
          </span>
        </div>
        <div>
          <FontAwesomeIcon icon={faStar} className="fa-5x text-logo-second" />
          <span className="text-logo-second font-semibold text-2xl">2023</span>
          <span className="text-black font-semibold text-xl">
            Most Houses Sold
          </span>
        </div>
        <div>
          <FontAwesomeIcon icon={faAward} className="fa-5x text-logo-second" />
          <span className="text-logo-second font-semibold text-2xl">2019</span>
          <span className="text-black font-semibold text-xl">
            Favourite Realtor{" "}
          </span>
        </div>
      </div>
      <div className="bg-logo-second p-20 mx-auto mt-16 flex flex-col md:flex-row gap-y-10 gap-x-14 max-w-[900px]">
        <img src={getCurrentHeadshot()} className="min-w-52" />
        <div>
          <h1 className="font-mont text-white text-2xl italic pb-4">
            "{getCurrentQuote()}"
          </h1>
          <p className="text-white mt-3 text-xl font-mont font-semibold">
            {getCurrentAgentName()}
          </p>
          <p className="text-white mt-1 text-lg font-mont">
            {getCurrentBrokerage()}
          </p>
        </div>
      </div>
      <h1 className="text-4xl text-black py-8 font-mont text-center mt-6">
        TESTIMONIALS
      </h1>

      <div className="mx-auto max-w-[800px] px-[15%] sm:px-[10%] xl:px-[5%] h-[45vh] sm:h-[35vh]">
        <Swiper
          style={{ "--swiper-pagination-color": "rgb(71, 71, 71)" }}
          slidesPerView={1}
          pagination={{
            clickable: true,
          }}
          loop={true}
          loopPreventsSliding={true}
          // autoplay={{
          //     delay: 10000,
          //     disableOnInteraction: true,
          // }}
          modules={[Pagination, Autoplay]}
        >
          {constants.TESTIMONIALS?.map((t, index) => (
            <SwiperSlide key={index}>
              <div className="swipe-slide h-[45vh] sm:h-[35vh]">
                {/* <div style={{overflowClipMargin: 'unset'}} className="rounded-full w-20 h-20 overflow-clip">
                                        <img className='w-full h-full object-cover' src={t.image} />
                                    </div> */}
                <img className="w-20" src={t.image} />
                <span className="text-black font-bold text-2xl">{t.name}</span>

                <span className="font-semibold text-med-grey text-lg italic">
                  "{t.quote}"
                </span>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <h1 className="text-4xl text-black py-8 font-mont text-center mt-6">
        RECENT LISTINGS
      </h1>
      <div className="grid listing-grid gap-9 max-w-[1100px] mx-auto px-7">
        {listings?.map((prop) => (
          <Link className="w-full h-full" to={`listing/${prop?.ListingKey}`}>
            <div className="relative w-full aspect-[5/3] sm:aspect-[4/3] overflow-hidden">
              <img
                className="w-full h-full object-cover"
                src={getListingImage(prop?.ListingKey)}
              />
              <div className="px-2 py-1 bg-logo-second absolute bottom-0 w-full max-h-[56px]">
                <span className=" text-white text-sm font-mont font-semibold">
                  {prop?.CrossStreet}
                </span>
              </div>
            </div>
            {/* <ReactImageGallery items={formatted[prop.ListingKey] ?? [{original: placeholder, thumbnail: placeholder}]} 
                        showThumbnails={false}
                        showPlayButton={false}
                        showFullscreenButton={false}
                        />
                        <div className="px-2 py-1 bg-logo-second  bottom-0 w-full z-10">
                                <span className=" text-white text-sm font-mont font-semibold">{prop.CrossStreet}</span>
                            </div> */}
          </Link>
        ))}
      </div>
      <h1 className="text-4xl text-black py-8 font-mont text-center mt-6">
        USEFUL TOOLS
      </h1>
      <div className="grid md:grid-cols-3 gap-10 child:overflow-hidden max-w-[1100px] mx-auto px-7 mb-10">
        <Link to={"/calculator"}>
          <div className="overflow-hidden h-96">
            <img className="h-full w-full object-cover" src={calc} />
          </div>
          <h3 className="text-2xl text-black pt-5 pb-2 font-mont">
            Mortgage Calculator
          </h3>
          <span className="font-mont">
            Find a price range that works with your budget by using our Mortgage
            Calculator
          </span>
        </Link>
        <Link to="/real-estate-reports">
          <div className="overflow-hidden h-96">
            <img className="h-full w-full object-cover" src={report} />
          </div>
          <h3 className="text-2xl text-black pt-5 pb-2 font-mont">
            Real Estate Reports
          </h3>
          <span className="font-mont">
            Gain valuable insights into the market with our free Real Estate
            Reports
          </span>
        </Link>
        <Link to="/evaluation">
          <div className="overflow-hidden h-96">
            <img className="h-full w-full object-cover" src={house} />
          </div>
          <h3 className="text-2xl text-black pt-5 pb-2 font-mont">
            Home Evaluation
          </h3>
          <span className="font-mont">
            How much is your home worth in today's market? Find out with our
            free Home Evaluation
          </span>
        </Link>
      </div>

      <div className="w-full bg-light-grey flex flex-wrap gap-x-16 gap-y-10 justify-center py-16 px-7">
        <div>
          <h4 className="font-mont font-semibold text-black text-lg min-w-[20vw]">
            Questions? I'm Here To Help
          </h4>
          <form onSubmit={submitForm} className="mt-6 flex flex-col">
            <div className="form-item">
              <label className="form-label">Name*</label>
              <input 
                className={`form-input ${formErrors.Name ? 'border-red-500' : ''}`}
                name="Name"
                placeholder="Full Name"
                value={formData.Name}
                onChange={handleInputChange}
              />
              {formErrors.Name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.Name}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Email*</label>
              <input
                className={`form-input ${formErrors["Email Address"] ? 'border-red-500' : ''}`}
                name="Email Address"
                placeholder="Email Address"
                value={formData["Email Address"]}
                onChange={handleInputChange}
              />
              {formErrors["Email Address"] && (
                <p className="text-red-500 text-sm mt-1">{formErrors["Email Address"]}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Message*</label>
              <textarea
                className={`form-input resize-none h-32 ${formErrors.Message ? 'border-red-500' : ''}`}
                name="Message"
                placeholder="Your Message"
                value={formData.Message}
                onChange={handleInputChange}
              />
              {formErrors.Message && (
                <p className="text-red-500 text-sm mt-1">{formErrors.Message}</p>
              )}
            </div>
            <button 
              type="submit" 
              className="btn-tertiary self-start px-10"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
        <div>
          <iframe
            class="contact-map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2885.1670922064877!2d-79.3108446!3d43.686289499999994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4cc14ec855631%3A0xb3d0feb68a2d0ed3!2s85%20Cedarvale%20Ave%2C%20Toronto%2C%20ON%20M4C%204J7!5e0!3m2!1sen!2sca!4v1730912488107!5m2!1sen!2sca"
            height="365"
            width={`${windowSize > 900 ? "500" : "300"}`}
            style={{ border: 0 }}
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>

          <div className="flex flex-col gap-y-2 text-black mt-6">
            <h2 className="text-black text-2xl">{getCurrentAgentName()}</h2>
            <p className="italic">
              {getCurrentAgentTitle()}, {getCurrentBrokerage()}
            </p>
            <p>
              <FontAwesomeIcon icon={faPhone} className="text-blue-800 fa-lg" />{" "}
              {getCurrentAgentNumber()}{" "}
            </p>
            <p>
              <FontAwesomeIcon
                icon={faEnvelope}
                className="text-blue-800 fa-lg"
              />{" "}
              {getCurrentAgentEmail()}{" "}
            </p>
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-black bg-opacity-80 text-white p-4 rounded-lg max-w-sm">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-bold">Global State Debug</h3>
            <button 
              onClick={() => setShowDebug(!showDebug)}
              className="text-xs bg-blue-600 px-2 py-1 rounded"
            >
              {showDebug ? 'Hide' : 'Show'}
            </button>
          </div>
          {showDebug && (
            <div className="text-xs">
              <div><strong>User ID:</strong> {globalState.getUserId() || 'Not Set'}</div>
              <div><strong>Agent Name:</strong> {getCurrentAgentName()}</div>
              <div><strong>Agent Title:</strong> {getCurrentAgentTitle()}</div>
              <div><strong>Agent Number:</strong> {getCurrentAgentNumber()}</div>
              <div><strong>Agent Email:</strong> {getCurrentAgentEmail()}</div>
              <div><strong>Brokerage:</strong> {getCurrentBrokerage()}</div>
              <div><strong>User Data:</strong> {globalState.getUserData() ? 'Set' : 'Not Set'}</div>
              <div><strong>Session:</strong> {globalState.getUserData() ? 'Saved' : 'Not Saved'}</div>
              <div><strong>Storage:</strong> sessionStorage</div>
              <div className="mt-2 text-xs text-gray-300">
                <div><strong>Data Flow:</strong></div>
                <div>1. URL UserId → UserData</div>
                <div>2. UserData → getAllUserParams</div>
                <div>3. getAllUserParams → UI</div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
